"use client";

import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
  type ReactNode,
} from "react";
import { Sun, Moon, Monitor } from "lucide-react";
import {
  type ThemeMode,
  type ThemePreset,
  type ThemeColors,
  type UserThemePreferences,
  themePresets,
  getStoredPreferences,
  storePreferences,
  getDefaultPreferences,
  getPreset,
  getResolvedMode,
  getThemeColors,
  applyTheme,
} from "@/lib/theme-system";

// ============================================================================
// Context Types
// ============================================================================

interface ThemeContextValue {
  // Current state
  preferences: UserThemePreferences;
  resolvedMode: "light" | "dark";
  colors: ThemeColors;
  currentPreset: ThemePreset;
  
  // Mode controls
  setMode: (mode: ThemeMode) => void;
  toggleMode: () => void;
  
  // Preset controls
  setPreset: (presetId: string) => void;
  
  // Custom color controls
  setCustomColor: (key: keyof ThemeColors, value: string) => void;
  resetCustomColors: () => void;
  
  // Accessibility controls
  setReducedMotion: (enabled: boolean) => void;
  setHighContrast: (enabled: boolean) => void;
  setFontSize: (size: "small" | "medium" | "large") => void;
  
  // Preview mode (for settings page)
  isPreviewMode: boolean;
  setPreviewMode: (enabled: boolean) => void;
  previewPreferences: UserThemePreferences | null;
  setPreviewPreferences: (prefs: UserThemePreferences | null) => void;
  applyPreview: () => void;
  
  // Reset
  resetToDefaults: () => void;
  
  // Utilities
  presets: ThemePreset[];
  isInitialized: boolean;
}

// ============================================================================
// Context
// ============================================================================

const ThemeContext = createContext<ThemeContextValue | null>(null);

// ============================================================================
// Provider Component
// ============================================================================

interface ThemeProviderProps {
  children: ReactNode;
  defaultMode?: ThemeMode;
  defaultPreset?: string;
  storageKey?: string;
}

export function ThemeProvider({
  children,
  defaultMode = "system",
  defaultPreset = "default",
}: ThemeProviderProps) {
  const [isInitialized, setIsInitialized] = useState(false);
  const [preferences, setPreferences] = useState<UserThemePreferences>(() => ({
    mode: defaultMode,
    presetId: defaultPreset,
    reducedMotion: false,
    highContrast: false,
    fontSize: "medium",
  }));
  
  // Preview mode state
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [previewPreferences, setPreviewPreferences] = useState<UserThemePreferences | null>(null);

  // Resolved values
  const resolvedMode = useMemo(
    () => getResolvedMode(isPreviewMode && previewPreferences ? previewPreferences.mode : preferences.mode),
    [preferences.mode, isPreviewMode, previewPreferences]
  );
  
  const currentPreset = useMemo(
    () => getPreset(isPreviewMode && previewPreferences ? previewPreferences.presetId : preferences.presetId),
    [preferences.presetId, isPreviewMode, previewPreferences]
  );
  
  const colors = useMemo(
    () => {
      const prefs = isPreviewMode && previewPreferences ? previewPreferences : preferences;
      return getThemeColors(prefs.presetId, resolvedMode, prefs.customColors);
    },
    [preferences, resolvedMode, isPreviewMode, previewPreferences]
  );

  // Initialize from localStorage (use layout effect to avoid flash)
  useEffect(() => {
    // Use requestAnimationFrame to defer state update
    const frame = requestAnimationFrame(() => {
      const stored = getStoredPreferences();
      if (stored) {
        setPreferences(stored);
      }
      setIsInitialized(true);
    });
    return () => cancelAnimationFrame(frame);
  }, []);

  // Apply theme whenever preferences change
  useEffect(() => {
    if (!isInitialized) return;
    
    const prefsToApply = isPreviewMode && previewPreferences ? previewPreferences : preferences;
    applyTheme(prefsToApply);
    
    // Only store if not in preview mode
    if (!isPreviewMode) {
      storePreferences(preferences);
    }
  }, [preferences, isInitialized, isPreviewMode, previewPreferences]);

  // Listen for system theme changes
  useEffect(() => {
    if (typeof window === "undefined") return;

    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handleChange = () => {
      const activePrefs = isPreviewMode && previewPreferences ? previewPreferences : preferences;
      if (activePrefs.mode === "system") {
        applyTheme(activePrefs);
      }
    };

    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, [preferences, isPreviewMode, previewPreferences]);

  // Mode controls
  const setMode = useCallback((mode: ThemeMode) => {
    setPreferences((prev) => ({ ...prev, mode }));
  }, []);

  const toggleMode = useCallback(() => {
    const currentResolved = getResolvedMode(preferences.mode);
    const newMode = currentResolved === "dark" ? "light" : "dark";
    setPreferences((prev) => ({ ...prev, mode: newMode }));
  }, [preferences.mode]);

  // Preset controls
  const setPreset = useCallback((presetId: string) => {
    setPreferences((prev) => ({ ...prev, presetId, customColors: undefined }));
  }, []);

  // Custom color controls
  const setCustomColor = useCallback((key: keyof ThemeColors, value: string) => {
    setPreferences((prev) => ({
      ...prev,
      customColors: { ...prev.customColors, [key]: value },
    }));
  }, []);

  const resetCustomColors = useCallback(() => {
    setPreferences((prev) => ({ ...prev, customColors: undefined }));
  }, []);

  // Accessibility controls
  const setReducedMotion = useCallback((enabled: boolean) => {
    setPreferences((prev) => ({ ...prev, reducedMotion: enabled }));
  }, []);

  const setHighContrast = useCallback((enabled: boolean) => {
    setPreferences((prev) => ({ ...prev, highContrast: enabled }));
  }, []);

  const setFontSize = useCallback((size: "small" | "medium" | "large") => {
    setPreferences((prev) => ({ ...prev, fontSize: size }));
  }, []);

  // Preview controls
  const handleSetPreviewMode = useCallback((enabled: boolean) => {
    setIsPreviewMode(enabled);
    if (!enabled) {
      setPreviewPreferences(null);
      // Reapply saved preferences
      applyTheme(preferences);
    } else {
      // Initialize preview with current preferences
      setPreviewPreferences(preferences);
    }
  }, [preferences]);

  const applyPreview = useCallback(() => {
    if (previewPreferences) {
      setPreferences(previewPreferences);
      setIsPreviewMode(false);
      setPreviewPreferences(null);
    }
  }, [previewPreferences]);

  // Reset
  const resetToDefaults = useCallback(() => {
    const defaults = getDefaultPreferences();
    setPreferences(defaults);
    setIsPreviewMode(false);
    setPreviewPreferences(null);
  }, []);

  // Context value
  const contextValue = useMemo<ThemeContextValue>(
    () => ({
      preferences,
      resolvedMode,
      colors,
      currentPreset,
      setMode,
      toggleMode,
      setPreset,
      setCustomColor,
      resetCustomColors,
      setReducedMotion,
      setHighContrast,
      setFontSize,
      isPreviewMode,
      setPreviewMode: handleSetPreviewMode,
      previewPreferences,
      setPreviewPreferences,
      applyPreview,
      resetToDefaults,
      presets: themePresets,
      isInitialized,
    }),
    [
      preferences,
      resolvedMode,
      colors,
      currentPreset,
      setMode,
      toggleMode,
      setPreset,
      setCustomColor,
      resetCustomColors,
      setReducedMotion,
      setHighContrast,
      setFontSize,
      isPreviewMode,
      handleSetPreviewMode,
      previewPreferences,
      applyPreview,
      resetToDefaults,
      isInitialized,
    ]
  );

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
}

// ============================================================================
// Hook
// ============================================================================

export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
}

// ============================================================================
// Convenience Hooks
// ============================================================================

/**
 * Hook for just the theme mode
 */
export function useThemeMode() {
  const { resolvedMode, preferences, setMode, toggleMode } = useTheme();
  return { mode: resolvedMode, preferredMode: preferences.mode, setMode, toggleMode };
}

/**
 * Hook for just the color preset
 */
export function useThemePreset() {
  const { currentPreset, presets, setPreset } = useTheme();
  return { preset: currentPreset, presets, setPreset };
}

/**
 * Hook for accessibility preferences
 */
export function useAccessibility() {
  const {
    preferences,
    setReducedMotion,
    setHighContrast,
    setFontSize,
  } = useTheme();
  
  return {
    reducedMotion: preferences.reducedMotion,
    highContrast: preferences.highContrast,
    fontSize: preferences.fontSize,
    setReducedMotion,
    setHighContrast,
    setFontSize,
  };
}

// ============================================================================
// Theme Toggle Component
// ============================================================================



interface ThemeToggleProps {
  showLabel?: boolean;
  size?: "sm" | "md" | "lg";
  className?: string;
}

export function ThemeToggle({ showLabel = false, size = "md", className = "" }: ThemeToggleProps) {
  const { preferences, setMode } = useTheme();
  
  const sizes = {
    sm: "w-8 h-8",
    md: "w-10 h-10",
    lg: "w-12 h-12",
  };
  
  const iconSizes = {
    sm: "w-4 h-4",
    md: "w-5 h-5",
    lg: "w-6 h-6",
  };

  const modes: { mode: ThemeMode; icon: typeof Sun; label: string }[] = [
    { mode: "light", icon: Sun, label: "Light" },
    { mode: "dark", icon: Moon, label: "Dark" },
    { mode: "system", icon: Monitor, label: "System" },
  ];

  return (
    <div className={`flex items-center gap-1 p-1 bg-white/[0.02] rounded-xl ${className}`}>
      {modes.map(({ mode, icon: Icon, label }) => (
        <button
          key={mode}
          onClick={() => setMode(mode)}
          className={`
            ${sizes[size]} rounded-lg flex items-center justify-center gap-2 transition-all
            ${preferences.mode === mode 
              ? "bg-amber-500 text-white shadow-lg" 
              : "text-slate-400 hover:text-white hover:bg-white/[0.05]"
            }
          `}
          title={label}
        >
          <Icon className={iconSizes[size]} />
          {showLabel && <span className="text-sm font-medium">{label}</span>}
        </button>
      ))}
    </div>
  );
}

// ============================================================================
// Quick Theme Toggle (just light/dark)
// ============================================================================

interface QuickThemeToggleProps {
  className?: string;
}

export function QuickThemeToggle({ className = "" }: QuickThemeToggleProps) {
  const { toggleMode, resolvedMode } = useTheme();
  
  return (
    <button
      onClick={toggleMode}
      className={`
        w-10 h-10 rounded-xl flex items-center justify-center
        bg-white/[0.02] hover:bg-white/[0.05] transition-all
        text-slate-400 hover:text-white
        ${className}
      `}
      title={`Switch to ${resolvedMode === "dark" ? "light" : "dark"} mode`}
    >
      {resolvedMode === "dark" ? (
        <Sun className="w-5 h-5" />
      ) : (
        <Moon className="w-5 h-5" />
      )}
    </button>
  );
}
